import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import InformationCircle from '@ingka/ssr-icon/paths/information-circle';
import { ComponentIds } from '@inter-ikea-core/core';
import { List } from 'components/ProductSidebar/menus/menuComponents';
import SelectInSceneInfo from 'components/SelectInSceneInfo/SelectInSceneInfo';
import { useCore } from 'contexts/core';
import {
  getFilterState,
  getMarketProducts,
  getSelectedGroupId,
  getConfigSorting,
  BatchSortFn
} from 'ducks/products';
import { getAllFrameGroups } from 'rangeConfig/shared/utils/inScene';
import { useIntl } from 'react-intl';
import Box from 'ui/Box';
import SkapaInlineMessage, { InlineMessageVariantEnum } from 'ui/InlineMessage/SkapaInlineMessage';
import InteriorSuggestionMultipleFrame, {
  FrameAndInteriorSuggestions
} from './InteriorSuggestionMultipleFrame';
import { InteriorSuggestionTypes } from './InteriorSuggestions';
import { getInteriorSuggestionsFromRuleSet } from './InteriorSuggestionsMultiFrameRules';
import {
  createFakeProductsForFramesAndSuggestionSet,
  filterInteriorSuggestions,
  filterSuggestionsOnUserFilters,
  getSelectedFilters,
  sortFramesByXPosition
} from './InteriorSuggestionsMultiFrameUtils';
import SelectInSceneImage from './images/select_pax_frame_in_scene.png';
import { isMobile } from 'utils/platform';
import { ProductDesign, ProductInfoComponent } from '@inter-ikea-storageone/range';
import {
  KompisCarousel,
  KompisCarouselItem,
  KompisPill
} from '@inter-ikea-kompis/react-components';
import { e2e } from 'utils/queryUtils';
import CarouselLayoutEnum from '@inter-ikea-kompis/component-carousel/lib/enums/CarouselLayoutEnum';
import { SkapaTheme } from '@inter-ikea-kompis/themes';
import styled from 'styled-components';
import MobileDrawer from 'components/MobileDrawer/MobileDrawer';
import PillSizeOptionsEnum from '@inter-ikea-kompis/component-pill/lib/enums/PillSizeOptionsEnum';
import { useLocation } from 'react-router-dom';
import { AppRoutes } from 'routes';
import ProductSortFilterWidget from 'rangeConfig/shared/widgets/ProductSortFilterWidget';
import { BatchType, ProductBatch } from 'utils/batch';
import { useWidget } from 'contexts/widget';

const CarouselWrapper = styled.ul`
  max-width: 100vw;
  width: 100%;
  padding: 0 ${({ theme }) => theme.space.md};
`;

const PillWrapper = styled.div`
  min-width: 115px;
  padding-left: ${({ theme }) => theme.space.s};
`;

export default function InteriorSuggestionsMultiFrame() {
  const products = useSelector(getMarketProducts);
  const coreApi = useCore();
  const filterState = useSelector(getFilterState);
  const selectedGroupId = useSelector(getSelectedGroupId);
  const { configId } = useWidget();
  const sorting = useSelector(getConfigSorting(configId));
  const { formatMessage } = useIntl();
  const [showMobileDrawer, setShowMobileDrawer] = useState(false);
  // If current page is summary, we should hide the mobile drawer as that causes the summary page to not be usable on mobile devices
  const currentPageIsSummary = useLocation().pathname === AppRoutes.SUMMARY;

  const groupedFrames = getAllFrameGroups(
    coreApi,
    coreApi.entities.getByComponent(ComponentIds.FRAME_COMPONENT_ID)
  );
  //Find fist group with more than 1 frame
  const firstFoundGroup = groupedFrames.find(g => g.length > 1);
  const userSelectedFilters = getSelectedFilters(filterState, selectedGroupId);
  // We need to use current design as key for component that renders the props image.
  // Without it it is not re-rendered in correct in some cases.
  const currentDesign = filterState(selectedGroupId, 'design').design;

  const interiorSuggestionProducts = useMemo(() => {
    const getFrameColor = () => {
      const frames = coreApi.entities
        .getAll()
        .filter(e => e.hasComponent(ComponentIds.FRAME_COMPONENT_ID));
      const defaultColor =
        frames.length > 0
          ? (frames[0].getComponent<ProductInfoComponent>(ProductInfoComponent.id)
              ?.design as ProductDesign)
          : ProductDesign.WHITE;
      return defaultColor;
    };

    return products.filter(
      p =>
        InteriorSuggestionTypes.includes(p.type) &&
        p.dimensions &&
        p.design === (!isMobile() ? currentDesign : getFrameColor())
    );
  }, [coreApi.entities, currentDesign, products]);

  const frameGroupWidth = coreApi.entities.getOuterMeasurements()?.width;
  if (!firstFoundGroup || !frameGroupWidth) return <></>;
  const framesWithLocation = sortFramesByXPosition(coreApi, firstFoundGroup);

  const suggestionsFromRuleSet = getInteriorSuggestionsFromRuleSet(
    framesWithLocation.map(f => f.dimensions.width)
  );

  let filteredSuggestions = filterInteriorSuggestions(
    coreApi,
    interiorSuggestionProducts,
    suggestionsFromRuleSet,
    framesWithLocation
  );

  if (!isMobile() && (!filteredSuggestions || filteredSuggestions?.length === 0)) {
    return (
      <SelectInSceneInfo
        message="storageone.paxSelectFrameInSceneChatBubbleMessage"
        image={SelectInSceneImage}
      />
    );
  }

  filteredSuggestions = filterSuggestionsOnUserFilters(filteredSuggestions, userSelectedFilters);

  const fakeProductsSuggestions = [] as FrameAndInteriorSuggestions[][];

  // We use max 8 suggestions
  filteredSuggestions.slice(0, 8).forEach(fs => {
    const suggestions = createFakeProductsForFramesAndSuggestionSet(framesWithLocation, fs);

    fakeProductsSuggestions.push(suggestions);
  });

  if (sorting?.compare)
    fakeProductsSuggestions.sort((sa, sb) =>
      ProductBatch.comparison(sorting.compare as BatchSortFn, BatchType.Group)(
        sa.map(fs => fs.suggestion),
        sb.map(fs => fs.suggestion)
      )
    );

  const wideCards = !isMobile() && (frameGroupWidth || 0) > 1500;

  const ListSuggestions = ({ showWide }: { showWide: boolean }) => {
    return fakeProductsSuggestions.map((fs, i) => (
      <InteriorSuggestionMultipleFrame
        // We use currentDesign as key. Without it switching between colors in
        // color picker resulted in canvases with mixed colors.
        key={`frameMultiProps_${currentDesign}_${i}`}
        frames={fs}
        menu={selectedGroupId}
        wideCards={showWide}
      />
    ));
  };

  return isMobile() ? (
    <>
      <CarouselWrapper>
        <KompisCarousel showArrows={!e2e()} layout={CarouselLayoutEnum.overflow} theme={SkapaTheme}>
          <ListSuggestions showWide={false} />
          <KompisCarouselItem>
            <PillWrapper>
              <KompisPill
                onSelect={() => setShowMobileDrawer(true)}
                pillSize={PillSizeOptionsEnum.small}
                label={formatMessage({ id: 'storageone.mobileSwiperViewMore' })}
                theme={SkapaTheme}
              />
            </PillWrapper>
          </KompisCarouselItem>
        </KompisCarousel>
      </CarouselWrapper>
      <MobileDrawer
        open={showMobileDrawer && !currentPageIsSummary}
        title={formatMessage({ id: 'storageone.interiorSuggestionsCombinationsTabTitle' })}
        onDismiss={() => setShowMobileDrawer(false)}
      >
        <Box mx="md" mt="md">
          <ListSuggestions showWide={true} />
        </Box>
      </MobileDrawer>
    </>
  ) : (
    <>
      <Box mt="l" mb="l">
        <SkapaInlineMessage
          body={formatMessage({
            id: 'storageone.interiorSuggestionsMultiCombinationsFrameSelectionInfo'
          })}
          variant={InlineMessageVariantEnum.INFORMATIVE}
          ssrIcon={InformationCircle}
          subtle
        />
      </Box>
      <ProductSortFilterWidget />
      <List style={{ marginTop: '1rem' }}>
        {fakeProductsSuggestions.map((fs, i) => (
          <InteriorSuggestionMultipleFrame
            // We use currentDesign as key. Without it switching between colors in
            // color picker resulted in canvases with mixed colors.
            key={`frameMultiProps_${currentDesign}_${i}`}
            frames={fs}
            menu={selectedGroupId}
            wideCards={wideCards}
          />
        ))}
      </List>
    </>
  );
}
